<script setup>
import { ref, defineProps, computed } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import api from '../services/api.js'

const props = defineProps(['field'])

// 日付（デフォルト: 1ヶ月前～当日）
const dateFrom = ref(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30日前
const dateTo = ref(new Date()); // 当日

const analyzing = ref(false);
const analysisResult = ref('');
const error = ref('');
const progressMessage = ref(''); // 進捗メッセージ
const showSettings = ref(true); // 設定パネルの表示状態

// 指定期間内の画像URLを取得
const getFilteredImageURLs = computed(() => {
  if (!props.field || !dateFrom.value || !dateTo.value) {
    return {
      RGB: [],
      NDVI: [],
      GNDVI: [],
      LCI: [],
      NDRE: [],
      OSAVI: []
    };
  }

  const startDateStr = formatDate(dateFrom.value);
  const endDateStr = formatDate(dateTo.value);

  const shootingDays = props.field?.ShootingDays?.find(e => e.id === "DJI");
  const imageTypes = ['RGB', 'NDVI', 'GNDVI', 'LCI', 'NDRE', 'OSAVI'];
  const result = {};

  imageTypes.forEach(type => {
    result[type] = [];
    if (shootingDays && shootingDays.Days) {
      shootingDays.Days.forEach(day => {
        // 日付が範囲内かチェック
        if (day >= startDateStr && day <= endDateStr) {
          const url = `https://sams-famisuke-dev-google-map-images.s3.ap-northeast-1.amazonaws.com/DJI/${props.field.id}/${type}/${day}.png`;
          result[type].push({ date: day, url: url });
        }
      });
    }
  });

  return result;
});

// 日付フォーマット（YYYYMMDD）
function formatDate(date) {
  if (!date) return '';
  
  // Dateオブジェクトに変換
  const d = date instanceof Date ? date : new Date(date);
  
  if (isNaN(d.getTime())) return ''; // 無効な日付の場合
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

// AI分析実行
async function analyzeWithAI() {
  analyzing.value = true;
  error.value = '';
  analysisResult.value = '';
  progressMessage.value = '';

  try {
    const imageURLs = getFilteredImageURLs.value;
    
    // 画像が存在するかチェック
    const totalImages = Object.values(imageURLs).reduce((sum, arr) => sum + arr.length, 0);
    if (totalImages === 0) {
      throw new Error('指定された期間内に画像が見つかりません');
    }

    // 日付をYYYYMMDD形式に変換
    const startDate = formatDate(dateFrom.value);
    const endDate = formatDate(dateTo.value);

    // バックエンドAPI呼び出し（ポーリング処理）
    const response = await api.analyzeFieldWithPolling(
      props.field.id, 
      startDate, 
      endDate,
      (progress) => {
        // 進捗更新コールバック
        progressMessage.value = progress.message;
      }
    );
    
    analysisResult.value = response.analysis;
    progressMessage.value = ''; // 完了したら進捗メッセージをクリア
    showSettings.value = false; // 分析完了後は設定パネルを折りたたむ

  } catch (err) {
    console.error('AI分析エラー:', err);
    error.value = err.response?.data?.error || err.message || '分析中にエラーが発生しました';
    progressMessage.value = '';
  } finally {
    analyzing.value = false;
  }
}
</script>

<template>
  <div class="p-3">
    <h6 class="mb-3">
      <i class="bi bi-robot"></i> AI分析（ChatGPT）
    </h6>

    <!-- 設定パネル折りたたみボタン -->
    <div class="mb-3">
      <button 
        class="btn btn-outline-secondary btn-sm w-100" 
        @click="showSettings = !showSettings"
        type="button"
      >
        <i :class="showSettings ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
        {{ showSettings ? '分析設定を閉じる' : '分析設定を開く' }}
      </button>
    </div>

    <!-- 分析設定パネル（折りたたみ可能） -->
    <div v-show="showSettings" class="settings-panel mb-3">
      <!-- 期間選択 -->
      <div class="mb-3">
        <label class="form-label fw-bold">分析期間</label>
        <div class="row g-2">
          <div class="col-6">
            <label class="form-label small">From（開始日）</label>
            <VueDatePicker
              v-model="dateFrom"
              :enable-time-picker="false"
              format="yyyy/MM/dd"
              :max-date="dateTo"
              locale="ja"
              placeholder="開始日"
            />
          </div>
          <div class="col-6">
            <label class="form-label small">To（終了日）</label>
            <VueDatePicker
              v-model="dateTo"
              :enable-time-picker="false"
              format="yyyy/MM/dd"
              :min-date="dateFrom"
              :max-date="new Date()"
              locale="ja"
              placeholder="終了日"
            />
          </div>
        </div>
      </div>

      <!-- 対象画像数表示 -->
      <div class="mb-3">
        <div class="card">
          <div class="card-body p-2">
            <h6 class="card-subtitle mb-2 text-muted">対象画像数</h6>
            <div class="row g-2">
              <div class="col-4" v-for="(images, type) in getFilteredImageURLs" :key="type">
                <div class="text-center">
                  <small class="text-muted d-block mb-1">{{ type }}</small>
                  <div class="badge bg-primary">{{ images.length }}枚</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分析ボタン -->
      <div class="mb-3">
        <button 
          class="btn btn-primary w-100" 
          @click="analyzeWithAI"
          :disabled="analyzing"
        >
          <span v-if="analyzing">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            分析中...
          </span>
          <span v-else>
            <i class="bi bi-magic"></i> AI分析を実行
          </span>
        </button>
      </div>
    </div>

    <!-- 進捗メッセージ表示 -->
    <div v-if="progressMessage" class="alert alert-info mb-3" role="alert">
      <i class="bi bi-hourglass-split"></i> {{ progressMessage }}
    </div>

    <!-- エラー表示 -->
    <div v-if="error" class="alert alert-danger" role="alert">
      <i class="bi bi-exclamation-triangle"></i> {{ error }}
    </div>

    <!-- 分析結果表示 -->
    <div v-if="analysisResult" class="mb-3">
      <div class="card">
        <div class="card-header bg-success text-white">
          <h6 class="mb-0">
            <i class="bi bi-check-circle"></i> 分析結果
          </h6>
        </div>
        <div class="card-body" style="max-height: 70vh; overflow-y: auto;">
          <div class="analysis-result" v-html="formatMarkdown(analysisResult)"></div>
        </div>
      </div>
    </div>

    <!-- 使用上の注意 -->
    <div class="alert alert-info" role="alert">
      <small>
        <i class="bi bi-info-circle"></i> 
        <strong>ご注意:</strong> AI分析は画像URLに基づく推論です。実際の圃場確認と併用してください。
      </small>
    </div>
  </div>
</template>

<style scoped>
.settings-panel {
  transition: all 0.3s ease-in-out;
}

.analysis-result {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.8;
}

.analysis-result >>> h1,
.analysis-result >>> h2,
.analysis-result >>> h3 {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-weight: bold;
}

.analysis-result >>> h1 {
  font-size: 1.5rem;
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.analysis-result >>> h2 {
  font-size: 1.3rem;
  color: #0d6efd;
}

.analysis-result >>> h3 {
  font-size: 1.1rem;
  color: #6c757d;
}

.analysis-result >>> ul,
.analysis-result >>> ol {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

.analysis-result >>> code {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: monospace;
}

.analysis-result >>> strong {
  color: #198754;
}
</style>

<script>
export default {
  methods: {
    // Markdown風の簡易フォーマット（基本的なもの）
    formatMarkdown(text) {
      if (!text) return '';
      
      let html = text;
      
      // ヘッダー変換
      html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
      html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
      html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
      
      // 太字
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // リスト
      html = html.replace(/^- (.*$)/gim, '<li>$1</li>');
      html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
      
      // 改行
      html = html.replace(/\n/g, '<br>');
      
      return html;
    }
  }
}
</script>
